import React, { useEffect, useState } from 'react';

// Components
import PropertiesMobileSubnav from './PropertiesMobileSubnav';
import SearchPropertiesSaved from '../../../searches/SearchPropertiesSaved';
import ErrorHandler from '../../../_molecules/ErrorHandler';
import TrackPageView from '@tracking/TrackPageView';
import SectionHeader from '../../../_molecules/SectionHeader';
import SearchRefinementsProperty from '../../../_organisms/SearchRefinementsProperty';
import SectionBreadcrumb from '../../../_molecules/SectionBreadcrumb';
import SectionBreadcrumbs from '../../../_molecules/SectionBreadcrumbs';
import EmptyStateContainer from '@molecules/EmptyStateContainer';
import LoadingArea from '@molecules/LoadingArea';
import { BodyLarge, DivWithShadow, H3, LinkSmall, SectionWrapperInner, SectionWrapperOuter } from '@styled';

// Helpers
import { getTodayUnix } from '@general/dates';
import useAlgoliaProperties from '@hooks/search/useAlgoliaProperties';
import useSavedPropertiesActive from '../../../../hooks/useSavedPropertiesActive';
import useDocumentTitle from '@hooks/useDocumentTitle';

const PropertiesSavedPage = () => {
    const [savedPropertyIds, setSavedPropertyIds] = useState();
    const { data: savedProperties, loading } = useSavedPropertiesActive();
    useDocumentTitle('Saved Properties');

    // This is necessary because saved records are not always in sync with what's in Algolia,
    // leading to an empty Algolia search instead of the Empty State Container we want to show.
    const { properties: algoliaProperties } = useAlgoliaProperties(savedPropertyIds);

    useEffect(() => {
        if (!savedProperties) {
            return;
        }

        setSavedPropertyIds(savedProperties.filter(p => p.manager.status === 'Active').map(p => p.recordId));
    }, [savedProperties]);

    if (!savedPropertyIds) {
        return <LoadingArea />;
    }

    return (
        <ErrorHandler>
            <TrackPageView>
                <PropertiesMobileSubnav />

                <SectionWrapperOuter>
                    <SectionWrapperInner>
                        <SectionHeader title="My ICSC Properties" />

                        <div className="flex justify-content-between">
                            <SectionBreadcrumbs>
                                <SectionBreadcrumb route="/my-icsc/properties">Properties</SectionBreadcrumb>
                                <SectionBreadcrumb isActive>Saved Properties</SectionBreadcrumb>
                            </SectionBreadcrumbs>
                        </div>

                        <H3 className="my-3">Saved Properties</H3>
                        <BodyLarge>These are the ICSC properties that you’ve saved.</BodyLarge>

                        <div className="mt-4">
                            {loading && <LoadingArea />}

                            {algoliaProperties?.length === 0 && !loading && (
                                <EmptyStateContainer
                                    Body={() => 'Click on the star on any property card to save that property.'}
                                    header="You haven't saved any properties yet."
                                    ctaText="Find Properties"
                                    ctaHref="/my-icsc/properties/find"
                                    onCTAClick={() => {}}
                                    imageSrc=""
                                />
                            )}

                            {algoliaProperties?.length > 0 && !loading && (
                                <DivWithShadow className="p-3">
                                    <SearchPropertiesSaved
                                        additionalFilters={`expiresAtTimestamp >= ${getTodayUnix()}`}
                                        parentView="Properties"
                                        refinementComponent={SearchRefinementsProperty}
                                        showPagingToolbar
                                        showSearchBox
                                        showSearchOptions
                                        updateSearchStateUrl
                                    />
                                </DivWithShadow>
                            )}
                        </div>
                    </SectionWrapperInner>
                </SectionWrapperOuter>
            </TrackPageView>
        </ErrorHandler>
    );
};

export default PropertiesSavedPage;
